/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';
import { SearchResultAlertType } from '../../organisms/OSearchResults/index.types';
import { SearchPageAlerts } from '../../organisms/OSearchResults/index.constant';

// Interface for SearchResultAlert context data (for hook compatibility)
export interface SearchResultAlertContextType {
  searchResultAlerts: SearchResultAlertType;
  setSearchResultAlerts: (alerts: SearchResultAlertType) => void;
}

// Interface for store state
export interface SearchResultAlertState {
  searchResultAlerts: SearchResultAlertType;
}

// Interface for store actions
export interface SearchResultAlertActions {
  // Initialize the store with direct data (following standard pattern)
  searchResultAlertInitialization: (initialAlerts?: SearchResultAlertType) => void;

  // Update search result alerts
  setSearchResultAlerts: (alerts: SearchResultAlertType) => void;

  // Reset store to initial state
  resetSearchResultAlertStore: () => void;

  // Update individual alert properties
  updateSearchResultAlert: (key: keyof SearchResultAlertType, value: any) => void;

  // Bulk update functionality
  updateSearchResultAlerts: (updates: Partial<SearchResultAlertType>) => void;
}

// Combined store type
export type SearchResultAlertStore = SearchResultAlertState & SearchResultAlertActions;

// Initial state matching SearchPageAlerts.default
const initialState: SearchResultAlertState = {
  searchResultAlerts: SearchPageAlerts.default,
};

// Store creator following existing patterns
const searchResultAlertZustandStore: StateCreator<SearchResultAlertStore> = set => {
  return {
    ...initialState,

    // Store initialization method (following standard pattern)
    searchResultAlertInitialization: (initialAlerts?: SearchResultAlertType) => {
      set(
        produce(draft => {
          // Initialize with provided alerts or keep defaults
          if (initialAlerts !== undefined) {
            draft.searchResultAlerts = initialAlerts;
          }
        })
      );
    },

    // Update search result alerts (main method matching context API)
    setSearchResultAlerts: (alerts: SearchResultAlertType) => {
      set(
        produce(draft => {
          draft.searchResultAlerts = alerts;
        })
      );
    },

    // Update individual alert properties
    updateSearchResultAlert: (key: keyof SearchResultAlertType, value: any) => {
      set(
        produce(draft => {
          draft.searchResultAlerts[key] = value;
        })
      );
    },

    // Bulk update functionality for multiple properties
    updateSearchResultAlerts: (updates: Partial<SearchResultAlertType>) => {
      set(
        produce(draft => {
          draft.searchResultAlerts = {
            ...draft.searchResultAlerts,
            ...updates,
          };
        })
      );
    },

    // Reset store to initial state
    resetSearchResultAlertStore: () => {
      set({ ...initialState });
    },
  };
};

// Create the store using the existing createAppStore pattern
export const useSearchResultAlertZustandStore = createAppStore(searchResultAlertZustandStore, {
  usePersistentStore: false,
});

// Export with legacy names for backward compatibility if needed
export const useSearchResultAlertStore = useSearchResultAlertZustandStore;
